/**
 * ZonesDetailsPage (/zones/:id) — the app's main working view.
 *
 * Loads the zone (with populated locations + sub-zones) into Redux and
 * derives everything the map needs in updateLocations(): the zone circle,
 * per-location marker colors and flags, and sub-zone membership. Hosts two
 * side panels ("Adresser" / "Delzoner") next to MapboxMain and the Excel
 * exports (XLSX via SheetJS).
 *
 * Every address carries one of five states from utils/locationStatus.js, and
 * the state decides its colour. The state table lives there, not here,
 * because the chip row, the list icon and the map popup all read it. The one
 * deliberate exception is sub-zone mode, which overwrites `color` with the
 * sub-zone's own (see the note at that branch). Note what that exception is
 * NOT: `color` is a single field, so the override would reach the list icon
 * too — it does not, only because the address panel is not rendered in
 * sub-zone mode. `status` itself is never overwritten.
 * Only three states reach the map at all: the two without a position exist in
 * the list alone.
 * Locations without coordinates get NO marker (GF-34). They carry
 * `noLatLng: true`, which is what hasGeocode (the Geokod button), the map's
 * point filter, its popup/fly-to anchor and the sub-zone draw counter key
 * on. They used to be parked
 * at a dummy point in the Baltic Sea that doubled as the Geokod sentinel;
 * that fabricated position was read by operators as a wrong geocode.
 *
 * Live updates: subscribes over Socket.IO to the user's own id and refetches
 * the zone when the backend signals "locations.geocoding.completed".
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
// Checkbox används fortfarande av delzonspanelen, även sedan adresslistans
// "Visa endast röda" ersatts av tillståndschipsen.
import { Button, Spin, Checkbox, Tooltip } from "antd";
import { useMaintenanceStatus } from "components/Maintenance";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { filter } from "lodash";
import * as turf from "@turf/turf";
import socketIOClient from "socket.io-client";
import Cookies from "js-cookie";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";

import "./ZonesDetails.scss";
import { MapboxMain } from "components/Mapbox";
import {
  ArrowIcon,
  LocationCenterIcon,
  EditIcon,
  DownloadIcon,
} from "components/Icons";
import { doDetailZone, doReGeocodeZone } from "modules/zone/stores/thunk";
import { updateLocationInsideZone } from "modules/zone/stores/slice";
import { TOKEN_NAME } from "constants";
import {
  ALL_KEY,
  LOCATION_STATUSES,
  STATUS_BY_KEY,
  countByStatus,
  deriveLocationStatus,
  exportableLocations,
  filterLocations,
} from "utils/locationStatus";

const socketURI = process.env.REACT_APP_SOCKET_URI || "";

// Natural sort for sub-zone names so "Zon 2" sorts before "Zon 10".
function compareStringNumeric(a, b) {
  return a.localeCompare(b, undefined, {numeric: true});
}
const ZonesDetailsPage = () => {
  // Underhållsfönster: spärrar re-geocode medan importer är pausade.
  const { blockActive, blockReason } = useMaintenanceStatus();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [menuSelected, setmenuSelected] = useState("addresses");
  const [locationClicked, setLocationClicked] = useState();
  const [subZoneClicked, setSubZoneClicked] = useState();
  const { isFetching, item: zone } = useSelector((state) => state.zone);
  const locationUpdated = useSelector((state) => state.location.locationUpdated);
  const [locations, setLocations] = useState([]);
  const [circle, setCircle] = useState();
  const [line, setLine] = useState();
  const [subZonesChecked, setSubZonesChecked] = useState([]);
  const [stopsInNewZones, setStopsInNewZones] = useState();
  const [isConfirmedAddress, setIsConfirmedAddress] = useState(false);
  const [isGeocode, setIsGeocode] = useState(false);
  // Flera tillstånd kan vara valda samtidigt. Tom lista betyder inget filter.
  const [statusFilter, setStatusFilter] = useState([]);

  const auth = useSelector((state) => state.auth);
  const user = auth.userData;


  useEffect(() => {
    // SECURITY: the socket is created here (after login) rather than at
    // module load, and authenticates the handshake with the same JWT the
    // REST calls use. `auth` as a function is re-evaluated on every
    // (re)connection attempt, so a renewed token is picked up on reconnect.
    //
    // ⚠️ GF-50: this is a SECOND transport to our API, and utils/request.js —
    // which is what notices a dead session and logs the user out — cannot see
    // it. A rejected handshake here is an expired token that produces no 401
    // anywhere request.js can observe.
    //
    // It is covered today only by coincidence: the effect below dispatches
    // doDetailZone() on the same mount, that REST call gets the 401, and the
    // logout happens through it. Remove or defer that dispatch and this page
    // loses its session detection without anything failing visibly. If that
    // ever changes, handle `connect_error` here instead of relying on a
    // neighbour.
    const socket = socketIOClient(socketURI, {
      auth: (cb) => cb({ token: Cookies.get(TOKEN_NAME) }),
    });
    socket.on(user?.sub, (data) => {
      if (data.event === "locations.geocoding.completed" && id) {
        setIsGeocode(false);
        dispatch(doDetailZone(id));
      }
    });
    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(doDetailZone(id));
    // eslint-disable-next-line
  }, []);

  const renderData = () => {
    updateLocations(zone.subZones);
    setSubZonesChecked(zone.subZones);
  }

  useEffect(() => {
    if (zone) {
      renderData()
    }
    // eslint-disable-next-line
  }, [zone, menuSelected]);

  useEffect(() => {
    if (zone) {
      updateLocations(subZonesChecked);
    }
    // eslint-disable-next-line
  }, [subZonesChecked]);

  useEffect(() => {
    if (locationUpdated?.zone && locationUpdated?.zone === zone?._id) {
      updateZoneAfterUpdateLocation()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationUpdated])

  // Builds the display list for the map/side panel from the raw zone data:
  // index 0 is the zone center itself (orderNo 0), followed by every
  // location with color/ptsWithin/subZone/isStart/isEnd computed. `subZones`
  // is the currently CHECKED sub-zones (controls coloring, not membership).
  const updateLocations = (subZones) => {
    const locationsT = [
      {
        _id: zone._id,
        orderNo: 0,
        type: "zone",
        ptsWithin: true,
        address: zone.centerAddress,
        lat: zone.centerLat,
        lng: zone.centerLng,
        color: "FFC444",
      },
    ];
    const radius = zone.distanceRadius;
    const center = [zone.centerLng, zone.centerLat];
    const options = { steps: 500, units: "kilometers" };
    const circleT = turf.circle(center, radius, options);
    const lineT = turf.lineString(...circleT.geometry.coordinates);
    const polygon = turf.polygon([...circleT.geometry.coordinates]);
    let tempSubzonePolygons = zone.subZones.map(subZone => turf.polygon([subZone.points]));
    let orderNoIdx = 1;
    for (let index = 0; index < zone.locations.length; index++) {
      const s = zone.locations[index];
      const locationT = {
        ...s,
        type: "location",
        orderNo: index + orderNoIdx,
        hasAlternativeLocations: !!s.alternativeLocations?.length,
      };
      if (s.lat) {
        locationT.circleColor = locationT.updatedInZone ? "00C853" : "888888";
        locationT.circleBackgroundColor = locationT.updatedInZone
          ? "dff3e7"
          : "8883";
        const point = turf.point([s.lng, s.lat]);
        const ptsWithin = turf.booleanPointInPolygon(point, polygon);
        locationT.ptsWithin = ptsWithin;
        locationT.status = deriveLocationStatus({
          hasCoordinates: true,
          insideZone: ptsWithin,
          updatedInZone: !!locationT.updatedInZone,
          geocoding: !!locationT.geocoding,
        });
        locationT.color = STATUS_BY_KEY[locationT.status].color;
        if (menuSelected === "sub-zones") {
          // zone.subZones.forEach((subZone) => {
          //   const polygonSub = turf.polygon([subZone.points]);
          //   if (turf.booleanPointInPolygon(point, polygonSub)) {
          //     locationT.subZone = subZone;
          //     if (subZones.includes(subZone)) {
          //       locationT.color = subZone.color || "FFC444";
          //     }
          //   }
          // });
          for (let subZoneIdx = 0; subZoneIdx < zone.subZones.length; ++subZoneIdx) {
            if (turf.booleanPointInPolygon(point, tempSubzonePolygons[subZoneIdx])) {
              const subZone = zone.subZones[subZoneIdx];
              locationT.subZone = subZone;
              // Sub-zone mode paints markers by sub-zone, which overrides the
              // status colour on the MAP. `status` is left alone, so the chip
              // row and the list keep telling the truth about the address.
              if (subZones.includes(subZone)) {
                locationT.color = subZone.color || "FFC444";
              }
              if (locationT.lat === subZone.startLat && locationT.lng === subZone.startLng) {
                locationT.isStart = true;
              }
              if (locationT.lat === subZone.endLat && locationT.lng === subZone.endLng) {
                locationT.isEnd = true;
              }
              break;
            }
          }
        }
      } else {
        // No coordinates: no marker on the map (GF-34). lat/lng are left as
        // the API returned them (null/undefined) — the Excel export then
        // writes an empty cell instead of a fabricated position. `noLatLng`
        // is the flag the map keys on (see file header).
        locationT.status = deriveLocationStatus({
          hasCoordinates: false,
          insideZone: false,
          updatedInZone: !!locationT.updatedInZone,
          geocoding: !!locationT.geocoding,
        });
        locationT.color = STATUS_BY_KEY[locationT.status].color;
        locationT.noLatLng = true;
        // "?"-knappens färger sattes tidigare BARA i grenen ovan, alltså bara
        // för adresser som har en position. Det spelade ingen roll så länge
        // ingenting skrev alternativeLocations — knappen kunde aldrig visas på
        // en rad utan koordinat. Med LM-providerns granskningskandidater är det
        // precis tvärtom: en kandidat betyder per definition att raden saknar
        // position, så knappen renderades med `#undefined` som färg.
        locationT.circleColor = STATUS_BY_KEY[locationT.status].color;
        locationT.circleBackgroundColor = "fdecea";
      }
      locationsT.push(locationT);
    }
    // orderNoIdx += zone.locations.length;
    // if (menuSelected === "sub-zones") {
    //   zone.subZones.forEach(subZone => {
    //     if (subZone.startLat !== undefined && subZone.startLng !== undefined) {
    //       console.log("Adding start-marker");
    //       locationsT.push({
    //         _id: subZone._id + "_start",
    //         orderNo: orderNoIdx++,
    //         type: "start",
    //         ptsWithin: true,
    //         address: zone.centerAddress,
    //         lat: subZone.startLat,
    //         lng: subZone.startLng,
    //         subZone,
    //         color: subZone.color,
    //       });
    //     }
    //     if (subZone.endLat !== undefined && subZone.endLng !== undefined) {
    //       const newPoint = {
    //         _id: subZone._id + "_end",
    //         orderNo: orderNoIdx++,
    //         type: "end",
    //         ptsWithin: true,
    //         address: zone.centerAddress,
    //         lat: subZone.endLat,
    //         lng: subZone.endLng,
    //         subZone,
    //         color: subZone.color,
    //       };
    //       console.log("Adding end-marker", newPoint);
    //       locationsT.push(newPoint);
    //     }
    //   });
    // }
    setCircle(circleT);
    setLine(lineT);
    setLocations(locationsT);
  };

  const getLocationData = (location) => {
    const data = {
      Address: location.address,
      Latitude: location.lat,
      Longitude: location.lng,
      "Property Designation": location.propertyDesignation,
      "Real Estate Listing": location.realEstateListing,
      "Work Order Flag": location.workOrderFlag,
      "Registration Number": location.registrationNumber,
      "New Address": location.freeTextField1,
      "Free Text Field 2": location.freeTextField2,
      "Owner": location.owner,
      "Owner Number": location.ownerNumber,
      Adjusted: location.updatedInZone ? "Y" : "N",
    };
    return data;
  };

  // Excel export of the checked sub-zones. The "export_start_stop"
  // localStorage flag (default on, toggled in Settings) controls whether
  // each sub-zone's start/stop addresses are included as extra rows.
  const handleDownloadSubzones = () => {
    const subZonesData = [];
    subZonesChecked.forEach((subZone) => {
      if ((localStorage.getItem("export_start_stop") || "1") === "1") {
        if (subZone.startAddress) {
          subZonesData.push({
            "SubZone Name": subZone.name,
            Address: subZone.startAddress,
            Latitude: subZone.startLat,
            Longitude: subZone.startLng,
            Type: "start",
          });
        }
        if (subZone.endAddress) {
          subZonesData.push({
            "SubZone Name": subZone.name,
            Address: subZone.endAddress,
            Latitude: subZone.endLat,
            Longitude: subZone.endLng,
            Type: "end",
          });
        }
      }
      locations.forEach((location) => {
        if (location.subZone === subZone) {
          subZonesData.push({
            "SubZone Name": subZone.name,
            ...getLocationData(location),
          });
        }
      });
    });
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";
    const wsSolution = XLSX.utils.json_to_sheet(subZonesData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSolution, "SubZones");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(dataBlob, `SubZones-${Date.now()}` + fileExtension);
  };

  const updateZoneAfterUpdateLocation = async () => {
    await dispatch(updateLocationInsideZone({zone, locationUpdated }));
  }

  // Exporten följer chipsvalet: man får det LISTAN visar. Kartan filtreras
  // inte av chipsen, så med ett filter aktivt syns markörer som exporten
  // utelämnar. Zonens mittpunkt följer aldrig med — den ligger i samma array
  // som adresserna för att kunna klickas i listan, men den är en egenskap hos
  // zonen, inte en adress.
  //
  // Delzonsexporten (handleDownloadSubzones) rör vi INTE. Chipsraden finns
  // inte i Delzoner-läget, och valet överlever flikbytet, så ett filter man
  // inte ser skulle tyst begränsa den exporten. Det är precis den tysta
  // begränsning knappens antal finns till för att undvika.
  const handleDownloadLocations = () => {
    const locationsData = exportableLocations(locations, statusFilter).map(
      (location) => getLocationData(location)
    );
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";
    const wsSolution = XLSX.utils.json_to_sheet(locationsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSolution, "SubZones");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(dataBlob, `Locations-${Date.now()}` + fileExtension);
  };

  const onChangeCheckbox = (e, item) => {
    const subZonesCheckedT = [...subZonesChecked];
    if (e.target.checked) {
      subZonesCheckedT.push(item);
    } else {
      const index = subZonesCheckedT.indexOf(item);
      subZonesCheckedT.splice(index, 1);
    }
    setSubZonesChecked(subZonesCheckedT);
  };

  const handleClickReGeocode = () => {
    setIsGeocode(true);
    dispatch(doReGeocodeZone(zone._id))
  }

  const handleOpenConfirmAddress = (location) => {
    setLocationClicked(location);
    setIsConfirmedAddress(true);
  };

  if (!zone) return <Spin style={{ paddingTop: 100, width: "100%" }} />;
  if (zone._id !== id)
    return <Spin style={{ paddingTop: 100, width: "100%" }} />;
  // Geokod button: shown when at least one location still lacks coordinates.
  // Keyed on the noLatLng flag set in updateLocations (GF-34), not on a magic
  // coordinate. A guard that used to sit here — lodash `has` called with a
  // predicate (GF-36) — was dead code and is REMOVED rather than repaired:
  // made live with `some`, it would have returned a spinner forever for any
  // zone holding an ungeocoded address, because it read the raw
  // zone.locations. The view already has its own spinner for fetching and
  // re-geocoding (below) and grey list icons for geocoding in progress.
  const hasGeocode = locations.some((l) => l.noLatLng);
  // Counts feed the chip row. The zone centre is in `locations` but is not an
  // address, and countByStatus keeps it out of every number.
  const statusCounts = countByStatus(locations);
  const visibleLocations = filterLocations(locations, statusFilter);
  const antalAttExportera = exportableLocations(locations, statusFilter).length;

  return (
    <Spin spinning={isFetching || isGeocode}>
      <div className="Content ZonesDetailsPage">
        <MapboxMain
          zone={zone}
          circle={circle}
          setStopsInNewZones={setStopsInNewZones}
          stopsInNewZones={stopsInNewZones}
          line={line}
          locations={locations}
          locationClicked={locationClicked}
          setLocationClicked={setLocationClicked}
          subZoneClicked={subZoneClicked}
          setSubZoneClicked={setSubZoneClicked}
          menuSelected={menuSelected}
          subZonesChecked={subZonesChecked}
          isConfirmedAddress={isConfirmedAddress}
          onSetIsConfirmedAddress={setIsConfirmedAddress}
        />
        <div className="Breadcrumb">
          <Link to="/zones">Zoner</Link>
          <ArrowIcon />
          <span>{zone?.name}</span>
        </div>
        <div className="AddressesSubZones">
          <div className="AddressesSubZones__Menu">
            <Button
              className={`btn-addresses ${
                menuSelected === "addresses" && "active"
              }`}
              onClick={() => setmenuSelected("addresses")}
            >
              Adresser
            </Button>
            <Button
              className={`btn-sub-zones ${
                menuSelected === "sub-zones" && "active"
              }`}
              onClick={() => setmenuSelected("sub-zones")}
            >
              Delzoner
            </Button>
          </div>
          <div className="AddressesSubZones__Content">
            {menuSelected === "addresses" && (
              <div className="Addresses">
                <div className="Title">
                  <div className="Left">Adresser</div>
                  <div className="Right">
                    {/*
                      Andra vägen in i geokodning. Måste spärras tillsammans
                      med "Ny Zon" — annars blir skyddet inkonsekvent och
                      användaren nekas först efter klicket. UX, inte skydd:
                      backend avvisar fortfarande med 503.
                    */}
                    {!!hasGeocode && <Tooltip title={blockReason || ""}>
                      <span>
                        <Button
                          className="btn-export"
                          disabled={blockActive}
                          onClick={handleClickReGeocode}
                        >
                          Geokod
                        </Button>
                      </span>
                    </Tooltip>}
                    {/*
                      Antalet står i knappen när ett filter är aktivt. Utan det
                      är det osynligt att exporten är begränsad, och en tyst
                      begränsning är värre än ingen.

                      Spärrad vid noll rader, samma vakt som Delzoner-knappen
                      redan har. Skälet är inte kosmetiskt: json_to_sheet tar
                      kolumnrubrikerna ur första objektet, så en tom export ger
                      ett blad utan både rader OCH rubriker. Det går inte att
                      skilja från en trasig export.
                    */}
                    <Button
                      className="btn-export"
                      disabled={antalAttExportera === 0}
                      onClick={handleDownloadLocations}
                    >
                      {statusFilter.length > 0
                        ? `Exportera ${antalAttExportera}`
                        : "Exportera"}
                      <DownloadIcon />
                    </Button>
                  </div>
                </div>
                {/*
                  Chipsraden ersätter kryssrutan "Visa endast röda", som
                  blandade ihop två helt olika saker: en adress som ligger fel
                  och en som aldrig hittades. Det är GF-23.

                  Om antalen och hur färskt de är: de räknas om varje gång
                  `locations` byggs om, bland annat när zonen hämtas om. Den
                  omhämtningen sker vid montering och när backend signalerar
                  att en geokodningsREQUEST är klar. En request täcker upp till
                  1500 adresser, så för en normal zon står "Geokodas nu" stilla
                  och faller till noll i ETT steg när körningen är klar. Chipet
                  svarar alltså på "är det klart?", inte på "hur långt har det
                  kommit?". Chips med noll tonas ned men försvinner inte, så
                  raden inte hoppar medan man tittar på den.
                */}
                <div className="StatusFilter">
                  {[{ key: ALL_KEY, label: "Alla" }, ...LOCATION_STATUSES].map((s) => {
                    const count = statusCounts[s.key];
                    const isAll = s.key === ALL_KEY;
                    // "Alla" är markerad när ingenting annat är det, och
                    // fungerar som nollställning. Övriga chips växlar.
                    const selected = isAll
                      ? statusFilter.length === 0
                      : statusFilter.includes(s.key);
                    // Ett valt chip förblir klickbart även vid noll, så man
                    // alltid kan välja bort ett filter som just tömts — vilket
                    // är precis vad som händer när geokodningen blir klar.
                    const empty = count === 0 && !isAll && !selected;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        title={s.description || "Alla adresser i zonen"}
                        disabled={empty}
                        className={`chip ${selected ? "active" : ""} ${empty ? "empty" : ""}`}
                        onClick={() =>
                          setStatusFilter((valda) =>
                            isAll
                              ? []
                              : valda.includes(s.key)
                              ? valda.filter((k) => k !== s.key)
                              : [...valda, s.key],
                          )
                        }
                      >
                        {s.color && (
                          <span
                            className="dot"
                            style={{
                              borderColor: `#${s.color}`,
                              borderStyle: s.dashed ? "dashed" : "solid",
                              background: s.dashed ? "transparent" : `#${s.color}`,
                            }}
                          />
                        )}
                        {s.label}
                        <b>{count}</b>
                      </button>
                    );
                  })}
                </div>
                <div className="ListAddresses">
                  {/*
                    Tre lägen, inte två, och de måste hållas isär.
                    `locations` är tom bara innan updateLocations kört — då ska
                    ingenting påstås alls. Är zonen laddad innehåller listan
                    alltid minst mittpunkten, så en tom ZON syns på
                    adressantalet och inte på listans längd. Det var felet i
                    första försöket: villkoret gick på listans längd och kunde
                    därför bara utlösas i den frame där påståendet var falskt.
                  */}
                  {locations.length > 0 && statusCounts[ALL_KEY] === 0 && (
                    <div className="EmptyFilter">Zonen har inga adresser.</div>
                  )}
                  {statusCounts[ALL_KEY] > 0 && visibleLocations.length === 0 && (
                    <div className="EmptyFilter">
                      {statusFilter.length > 1
                        ? "Inga adresser i de valda tillstånden."
                        : "Inga adresser i det här tillståndet."}
                    </div>
                  )}
                  {visibleLocations.map((location) => (
                    <div
                      key={location.orderNo}
                      className={`Item ${
                        locationClicked?._id === location._id && "selecled"
                      }`}
                    >
                      <div className="Name">
                        {/*
                          Streckad ring = adressen har ingen position alls.
                          Formen bär skillnaden där färgen är svag: gul och
                          grå ligger nära varandra i luminans, men hamnar
                          aldrig i samma formgrupp. Se utils/locationStatus.js.
                        */}
                        <div
                          className="Icon"
                          style={{
                            border: `1px ${
                              STATUS_BY_KEY[location.status]?.dashed ? "dashed" : "solid"
                            } #${location.color}`,
                            color: `#${location.color}`,
                          }}
                        >
                          {location.orderNo === 0 ? (
                            <LocationCenterIcon />
                          ) : (
                            location.orderNo
                          )}
                        </div>
                        {location.hasAlternativeLocations && (
                          <Button
                            type="text"
                            className="circle-box"
                            style={{
                              padding: 0,
                              color: `#${location.circleColor}`,
                              backgroundColor: `#${location.circleBackgroundColor}`,
                            }}
                            onClick={() => handleOpenConfirmAddress(location)}
                          >
                            ?
                          </Button>
                        )}
                        {location.address}
                      </div>
                      <div
                        className="Edit"
                        onClick={() => {
                          setLocationClicked(location);
                          setIsConfirmedAddress(false);
                        }}
                      >
                        <EditIcon />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {menuSelected === "sub-zones" && (
              <div className="SubZones">
                <div className="Title">
                  <div className="Left">Delzoner</div>
                  <div className="Right">
                    <Button
                      disabled={!subZonesChecked.length}
                      className="btn-export"
                      onClick={handleDownloadSubzones}
                    >
                      Exportera
                      <DownloadIcon />
                    </Button>
                  </div>
                </div>
                <div style={{
                  padding: "0 16px",
                  marginBottom: ".5rem"
                }}>
                  <Checkbox checked={subZonesChecked.length === zone.subZones.length} onChange={e => {e.target.checked ? setSubZonesChecked([...zone.subZones]) : setSubZonesChecked([])}}><span style={{userSelect: "none"}}>Alla</span></Checkbox>
                </div>
                <div className="ListAddresses">
                  {[...zone.subZones].sort((a,b) => compareStringNumeric(a.name,b.name)).map((subZone, index) => (
                    <div
                      key={index}
                      className={`Item ${
                        subZoneClicked?._id === subZone._id && "selecled"
                      }`}
                    >
                      <div className="Name">
                        <Checkbox
                          checked={subZonesChecked.includes(subZone)}
                          onChange={(e) => onChangeCheckbox(e, subZone)}
                        />
                        <span
                          style={{
                            width: "24px",
                            height: "24px",
                            background: `#${subZone.color}`,
                            marginRight: "4px",
                            marginLeft: "4px",
                            borderRadius: "4px"
                          }}
                        />{" "}
                        {subZone.name} (
                        {filter(locations, (l) => l.subZone === subZone).length}{" "}
                        stopp)
                      </div>
                      <div
                        className="Edit"
                        onClick={() => setSubZoneClicked(subZone)}
                      >
                        <EditIcon />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default ZonesDetailsPage;
